import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, Crown, Home, AlertCircle, Loader2 } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/useCurrency";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

const Pricing = () => {
  const { subscribed, isTrialing, isFreeExploring, loading, user, createCheckout } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { formatPrice } = useCurrency();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [isProcessing, setIsProcessing] = useState(false);
  const trialEndedMessage = location.state?.message;
  const isNewUser = location.pathname === '/pricing' && !subscribed && !isTrialing;

  // Redirect users who shouldn't be on pricing page
  useEffect(() => {
    if (loading) return;
    
    // Redirect already-subscribed users to profile
    if (subscribed && !trialEndedMessage) {
      navigate('/profile');
      return;
    }
    
    // Redirect users in free exploration back to home (unless coming from trial ended message)
    if (isFreeExploring && !trialEndedMessage) {
      navigate('/');
    }
  }, [loading, subscribed, isFreeExploring, trialEndedMessage, navigate]);

  const handleSubscribe = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to subscribe",
      });
      navigate("/auth");
      return;
    }

    // Prevent double-clicks / duplicate checkout sessions
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // If user is in free exploration, mark trial as started before checkout
      if (isFreeExploring) {
        const trialEndsAt = new Date();
        trialEndsAt.setDate(trialEndsAt.getDate() + 14);
        
        await supabase
          .from("profiles")
          .update({
            has_started_trial: true,
            trial_start_date: new Date().toISOString(),
            trial_ends_at: trialEndsAt.toISOString(),
          })
          .eq("user_id", user.id);
      }
      
      await createCheckout(billingPeriod);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const premiumFeatures = [
    "Unlimited AI coaching with Aura",
    "Full symptom & progress tracking",
    "Advanced insights & analytics",
    "Doctor report generation",
    "Complete learning hub access",
    "Unlimited community access",
    "All SOS tools & techniques",
    "Full Myth Buster quiz library",
    "Personalized wellness plans",
    "Priority support"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-secondary/10 pt-20 pb-12 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-primary/10 pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10">
      <div className="container max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6 gap-2 relative z-20"
        >
          <Home className="h-4 w-4" />
          Back Home
        </Button>

        {trialEndedMessage && (
          <Alert className="mb-6 border-primary/50 bg-primary/5">
            <AlertCircle className="h-4 w-4 text-primary" />
            <AlertDescription className="text-foreground text-center font-serif text-base leading-relaxed">
              How are you liking our app? We hope you're finding it helpful and would love for you to stay on!
            </AlertDescription>
          </Alert>
        )}
        
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {trialEndedMessage ? 'Keep Your Wellness Journey Going' : 'Start Your Free Trial'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            {trialEndedMessage 
              ? 'Subscribe below to continue your access.'
              : 'Experience all premium features risk-free for 14 days. Enter your card to begin—no charges during trial.'
            }
          </p>
          {!trialEndedMessage && (
            <div className="max-w-3xl mx-auto mb-8 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Credit card required</strong> to start your trial (we won't charge you yet)</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Full access for 14 days</strong> to explore all features</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>Cancel anytime</strong> during trial with no charges</span>
                </p>
                <p className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span><strong>After 14 days:</strong> Your selected plan begins automatically</span>
                </p>
              </div>
            </div>
          )}
          
          <Tabs value={billingPeriod} onValueChange={(value) => setBillingPeriod(value as "monthly" | "annual")} className="w-fit mx-auto">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="annual">
                Annual
                <Badge variant="secondary" className="ml-2 bg-primary/10 text-primary">
                  Save $36
                </Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="max-w-md mx-auto">
          <Card className="relative border-primary shadow-lg">
            {!trialEndedMessage && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-4 py-1">
                  14-Day Free Trial
                </Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle className="flex items-center justify-center gap-2 text-3xl">
                <Crown className="w-8 h-8 text-primary" />
                {trialEndedMessage ? 'Continue Your Access' : 'Premium Access'}
              </CardTitle>
              <CardDescription className="text-center">
                {trialEndedMessage ? 'Keep enjoying all features' : 'All features included'}
              </CardDescription>
              <div className="pt-4 text-center">
                {billingPeriod === "monthly" ? (
                  <>
                    <span className="text-5xl font-bold">A$9.99</span>
                    <span className="text-muted-foreground text-lg">/month</span>
                  </>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <span className="text-5xl font-bold">A$89.99</span>
                      <span className="text-muted-foreground text-lg">/year</span>
                    </div>
                    <div className="text-sm">
                      <span className="line-through text-muted-foreground">A$119.88/year</span>
                      <span className="text-primary font-semibold ml-2 text-base">Save A$29.89</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      That's only A$7.50/month when you pay annually
                    </p>
                  </div>
                )}
              </div>
              {isTrialing && (
                <Badge variant="outline" className="w-fit mt-2">
                  Currently on free trial
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {premiumFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full mt-6 bg-gradient-to-r from-primary to-primary/80 h-12 text-lg"
                disabled={loading || isProcessing}
                onClick={handleSubscribe}
              >
                {isProcessing ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : subscribed ? (
                  "Manage Subscription"
                ) : trialEndedMessage ? (
                  "Subscribe Now"
                ) : (
                  "Start 14-Day Free Trial"
                )}
              </Button>
              <div className="text-center mt-4 space-y-3">
                {!trialEndedMessage && (
                  <div className="p-3 bg-secondary/30 rounded-md">
                    <p className="text-sm font-medium text-foreground">
                      How it works:
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter your card → Try free for 14 days → {billingPeriod === "monthly" ? "A$9.99/month" : "A$89.99/year"} charged after trial
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cancel before day 14 and pay nothing
                    </p>
                  </div>
                )}
                {subscribed && (
                  <p className="text-sm text-muted-foreground">
                    Cancel anytime from your profile
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Questions? We're here to help!
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
